import React from 'react';

const Speedometer = ({ score, size = 32 }) => {
  // Ensure score is within bounds
  const normalizedScore = Math.max(1, Math.min(10, score || 6));
  
  // Calculate angle for needle (0-180 degrees, where 0 is leftmost, 180 is rightmost)
  const angle = ((normalizedScore - 1) / 9) * 180;
  
  // Calculate colors for different zones
  const getZoneColor = (zoneStart, zoneEnd) => {
    if (normalizedScore >= zoneStart && normalizedScore <= zoneEnd) {
      return 'opacity-100';
    }
    return 'opacity-30';
  };
  
  // Dynamic colors based on score
  const needleColor = normalizedScore < 4 ? '#ef4444' : normalizedScore < 7 ? '#f59e0b' : '#10b981';
  const borderColor = normalizedScore < 4 ? '#ef4444' : normalizedScore < 7 ? '#f59e0b' : '#10b981';
  
  return (
    <div className="flex flex-col items-center" title={`Sentiment Score: ${normalizedScore.toFixed(1)}/10`}>
      <div className="relative" style={{ width: size, height: size * 0.6 }}>
        <svg 
          width={size} 
          height={size * 0.6} 
          viewBox="0 0 100 60" 
          className="overflow-visible"
        >
          {/* Background arc */}
          <path
            d="M 10 50 A 40 40 0 0 1 90 50"
            fill="none"
            stroke="#e5e7eb"
            strokeWidth="8"
            strokeLinecap="round"
          />
          
          {/* Red zone (1-3) */}
          <path
            d="M 10 50 A 40 40 0 0 1 35 20"
            fill="none"
            stroke="#ef4444"
            strokeWidth="8"
            strokeLinecap="round"
            className={getZoneColor(1, 3)}
          />
          
          {/* Yellow zone (4-6) */}
          <path
            d="M 35 20 A 40 40 0 0 1 65 20"
            fill="none"
            stroke="#f59e0b"
            strokeWidth="8"
            strokeLinecap="round"
            className={getZoneColor(4, 6)}
          />
          
          {/* Green zone (7-10) */}
          <path
            d="M 65 20 A 40 40 0 0 1 90 50"
            fill="none"
            stroke="#10b981"
            strokeWidth="8"
            strokeLinecap="round"
            className={getZoneColor(7, 10)}
          />
          
          {/* Center dot */}
          <circle
            cx="50"
            cy="50"
            r="3"
            fill={needleColor}
          />
          
          {/* Needle */}
          <line
            x1="50"
            y1="50"
            x2={50 + 30 * Math.cos((180 - angle) * Math.PI / 180)}
            y2={50 - 30 * Math.sin((180 - angle) * Math.PI / 180)}
            stroke={needleColor}
            strokeWidth="2"
            strokeLinecap="round"
            className="transition-all duration-500 ease-out"
          />
          
          {/* Needle tip */}
          <circle
            cx={50 + 30 * Math.cos((180 - angle) * Math.PI / 180)}
            cy={50 - 30 * Math.sin((180 - angle) * Math.PI / 180)}
            r="1.5"
            fill={needleColor}
            className="transition-all duration-500 ease-out"
          />
          
          {/* Scale marks */}
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((mark) => {
            const markAngle = ((mark - 1) / 9) * 180;
            const x1 = 50 + 35 * Math.cos((180 - markAngle) * Math.PI / 180);
            const y1 = 50 - 35 * Math.sin((180 - markAngle) * Math.PI / 180);
            const x2 = 50 + 40 * Math.cos((180 - markAngle) * Math.PI / 180);
            const y2 = 50 - 40 * Math.sin((180 - markAngle) * Math.PI / 180);
            
            return (
              <line
                key={mark}
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke="#6b7280"
                strokeWidth="1"
                opacity="0.6"
              />
            );
          })}
        </svg>
        
        {/* Score display */}
        <div 
          className="absolute bottom-0 left-1/2 transform -translate-x-1/2 text-xs font-bold"
          style={{ color: needleColor }}
        >
          {normalizedScore.toFixed(1)}
        </div>
      </div>
      
      {/* Status text */}
      <div className="text-xs text-gray-500 mt-1 font-medium">
        {normalizedScore < 4 ? 'Negative' : normalizedScore < 7 ? 'Neutral' : 'Positive'}
      </div>
    </div>
  );
};

export default Speedometer; 